// On-demand screen snapshot (docs/04; server signals via the response header
// `X-SeenCast-Command: snapshot` on a manifest/heartbeat 200 OR 304).
//
// Two capture paths:
//   - NATIVE (Android shell): window.SeenCastNative.captureScreen(requestId);
//     the shell calls back window.__seencastOnSnapshot(requestId, base64, w, h).
//     We own that dispatch — a requestId→promise map with a 15 s timeout.
//   - DOM (browser PWA, best-effort): the current <img>/<video> is drawn to a
//     canvas (same-origin blob URLs don't taint it); iframes (types url/html)
//     can't be rasterized → reported as `unsupported`.
//
// The whole thing is debounced to at most one capture per 60 s. All side effects
// (native bridge, DOM capture, upload, id/now) are injected so the debounce +
// decision + bridge-dispatch logic is unit-testable with no DOM and no server.

import type { ManifestItemType } from './types.ts';

export type SnapshotKind = 'native' | 'dom' | 'unsupported';

/**
 * DOM-capture decision by the on-screen item's manifest type: image/video rasterize
 * to a canvas (`dom`); 'url' and 'html' render in an iframe that cannot be captured
 * (`unsupported`).
 */
export function snapshotKindForType(type: ManifestItemType): 'dom' | 'unsupported' {
  return type === 'image' || type === 'video' ? 'dom' : 'unsupported';
}

// --- Native bridge -----------------------------------------------------------

interface SeenCastNative {
  captureScreen(requestId: string): void;
}

interface SnapshotWindow {
  SeenCastNative?: SeenCastNative;
  __seencastOnSnapshot?: (requestId: string, base64Jpeg: string, width: number, height: number) => void;
}

export interface NativeSnapshot {
  base64: string;
  width: number;
  height: number;
}

const NATIVE_TIMEOUT_MS = 15_000;

/**
 * Dispatch for the Android shell's async screen capture. Installs the global
 * callback lazily and maps each requestId to a pending promise, rejecting after
 * 15 s so a shell that never answers can't wedge the caller.
 */
export class NativeBridge {
  private pending = new Map<
    string,
    { resolve: (s: NativeSnapshot) => void; reject: (e: unknown) => void; timer: ReturnType<typeof setTimeout> }
  >();
  private installed = false;

  constructor(private win: SnapshotWindow = window as unknown as SnapshotWindow) {}

  /** True when the native shell exposes a capture hook (Android APK, not a browser). */
  available(): boolean {
    return typeof this.win.SeenCastNative?.captureScreen === 'function';
  }

  private install(): void {
    if (this.installed) return;
    this.installed = true;
    this.win.__seencastOnSnapshot = (requestId, base64Jpeg, width, height) => {
      const p = this.pending.get(requestId);
      if (!p) return; // unknown or already-timed-out request
      clearTimeout(p.timer);
      this.pending.delete(requestId);
      p.resolve({ base64: base64Jpeg, width, height });
    };
  }

  capture(requestId: string): Promise<NativeSnapshot> {
    this.install();
    return new Promise<NativeSnapshot>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(requestId);
        reject(new Error('native snapshot timeout'));
      }, NATIVE_TIMEOUT_MS);
      this.pending.set(requestId, { resolve, reject, timer });
      try {
        this.win.SeenCastNative!.captureScreen(requestId);
      } catch (e) {
        clearTimeout(timer);
        this.pending.delete(requestId);
        reject(e);
      }
    });
  }
}

// --- Controller --------------------------------------------------------------

const DEBOUNCE_MS = 60_000;

export interface DomCapture {
  blob: Blob;
  width: number;
  height: number;
}

export interface SnapshotDeps {
  native: NativeBridge;
  /** The on-screen item's media type, or null when idle / nothing playing. */
  currentType: () => ManifestItemType | null;
  /** Rasterize the current <img>/<video> (+ overlay). Browser-only; null if it couldn't. */
  captureDom: () => Promise<DomCapture | null>;
  /** Send the capture (native/dom = jpeg blob; unsupported = null body). */
  upload: (kind: SnapshotKind, body: Blob | null, width: number, height: number) => Promise<void>;
  makeId: () => string;
  /** base64 (native path) → jpeg Blob. Injected for testability. */
  base64ToBlob: (base64: string) => Blob;
  now?: () => number;
  onError?: (e: unknown) => void;
}

export class SnapshotController {
  private lastAt = Number.NEGATIVE_INFINITY;
  private now: () => number;

  constructor(private deps: SnapshotDeps) {
    this.now = deps.now ?? (() => Date.now());
  }

  /**
   * Handle a `snapshot` command. Debounced: a command seen within 60 s of the
   * last accepted one is ignored. Returns true if this call performed a capture.
   */
  async onCommand(): Promise<boolean> {
    const t = this.now();
    if (t - this.lastAt < DEBOUNCE_MS) return false;
    this.lastAt = t; // reserve the window up front so a slow/failed capture still debounces
    try {
      await this.capture();
    } catch (e) {
      this.deps.onError?.(e);
    }
    return true;
  }

  private async capture(): Promise<void> {
    const requestId = this.deps.makeId();

    if (this.deps.native.available()) {
      const s = await this.deps.native.capture(requestId);
      await this.deps.upload('native', this.deps.base64ToBlob(s.base64), s.width, s.height);
      return;
    }

    const type = this.deps.currentType();
    if (type === null || snapshotKindForType(type) === 'unsupported') {
      await this.deps.upload('unsupported', null, 0, 0);
      return;
    }
    const dom = await this.deps.captureDom();
    if (!dom) {
      await this.deps.upload('unsupported', null, 0, 0);
      return;
    }
    await this.deps.upload('dom', dom.blob, dom.width, dom.height);
  }
}
